import {
  cookieBannerLabels,
  cookieBannerSchema,
  fitAnalyticsSchema,
  footerSchema,
  globalNavigationLabelsSchema,
  globalNavigationSchema,
  headerSchema,
  helpSchema,
  menuSchema,
  socials,
  socialSchema,
  storeLocatorSchema,
  subMenuSchema,
} from '@integration-layer/schemas/GAEvents/navigation'
import z from 'zod'
import snakeCase from 'lodash/snakeCase'

// Type definitions for schema inference
type GlobalNavigationLabels = z.infer<typeof globalNavigationLabelsSchema>
type Socials = z.infer<typeof socials>
type CookieBannerLabels = z.infer<typeof cookieBannerLabels>

/**
 * Provides methods for tracking various navigation-related events in Google Analytics.
 *
 * @returns {Object} An object containing methods to dispatch different navigation events.
 *
 * @description This composable handles tracking of user interactions across different
 * navigation components, including global navigation, header, menu, store locator,
 * footer, social links, and more.
 *
 * @example
 * const {
 *   dispatchGlobalNavigationEvent,
 *   dispatchHeaderEvent
 * } = useGANavigationEvents()
 *
 * dispatchGlobalNavigationEvent('home')
 * dispatchHeaderEvent('logo')
 */
export const useGANavigationEvents = () => {
  // Enable debug mode based on runtime configuration
  const debug = !!useRuntimeConfig().public.ga4.debug

  /**
   * Tracks global navigation events.
   *
   * @param {GlobalNavigationLabels} label - The label of the global navigation item clicked.
   *
   * @description Sends a Google Analytics event when a global navigation item is clicked.
   */
  const dispatchGlobalNavigationEvent = (label: GlobalNavigationLabels) => {
    useGASendEvent(
      globalNavigationSchema,
      {
        event: 'GAevent',
        eventID: '005',
        eventCategory: 'global_navigation',
        eventAction: 'click_cta',
        eventLabel: label,
        eventElement01: '-',
        eventElement02: '-',
        eventElement03: '-',
      },
      { debug }
    )
  }

  /**
   * Tracks header element click events.
   *
   * @param {string} element - The header element that was clicked.
   *
   * @description Sends a Google Analytics event when a header element is clicked.
   */
  const dispatchHeaderEvent = (element: string) => {
    useGASendEvent(
      headerSchema,
      {
        event: 'GAevent',
        eventID: '006',
        eventCategory: 'header',
        eventAction: 'click_cta',
        eventLabel: 'strips',
        eventElement01: snakeCase(element),
        eventElement02: '-',
        eventElement03: '-',
      },
      { debug }
    )
  }

  /**
   * Tracks menu navigation events.
   *
   * @param {string[]} categories - An array of category levels navigated.
   *
   * @description Sends a Google Analytics event when menu categories are navigated,
   * with special handling for the EA7 brand.
   */
  const dispatchMenuEvent = (categories: string[]) => {
    useGASendEvent(
      menuSchema,
      {
        event: 'GAevent',
        eventID: '007',
        eventCategory: 'menu',
        eventAction: 'click_cta',
        eventLabel:
          snakeCase(categories?.[0]) === 'ea_7'
            ? 'EA7'
            : (snakeCase(categories?.[0]) ?? '-'),
        eventElement01: categories?.[1] ?? '-',
        eventElement02: categories?.[2] ?? '-',
        eventElement03: categories?.[3] ?? '-',
      },
      { debug }
    )
  }

  /**
   * Tracks store locator events.
   *
   * @description Sends a Google Analytics event when the store locator is accessed.
   */
  const dispatchStoreLocatorEvent = () => {
    useGASendEvent(
      storeLocatorSchema,
      {
        event: 'GAevent',
        eventID: '010',
        eventCategory: 'store_locator',
        eventAction: 'click_cta',
        eventLabel: 'Find a Boutique',
        eventElement01: '-',
        eventElement02: '-',
        eventElement03: '-',
      },
      { debug }
    )
  }

  /**
   * Tracks submenu navigation events.
   *
   * @param {string} label - The label of the submenu item clicked.
   *
   * @description Sends a Google Analytics event when a submenu item is clicked.
   */
  const dispatchSubMenuEvent = (label: string) => {
    useGASendEvent(
      subMenuSchema,
      {
        event: 'GAevent',
        eventID: '011',
        eventCategory: 'submenu',
        eventAction: 'click_cta',
        eventLabel: label,
        eventElement01: '-',
        eventElement02: '-',
        eventElement03: '-',
      },
      { debug }
    )
  }

  /**
   * Tracks footer navigation events.
   *
   * @param {string} category - The category of the footer item.
   * @param {string} label - The specific label of the footer item clicked.
   *
   * @description Sends a Google Analytics event when a footer item is clicked.
   */
  const dispatchFooterEvent = (category: string, label: string) => {
    useGASendEvent(
      footerSchema,
      {
        event: 'GAevent',
        eventID: '012',
        eventCategory: 'footer',
        eventAction: 'click_cta',
        eventLabel: category,
        eventElement01: label,
        eventElement02: '-',
        eventElement03: '-',
      },
      { debug }
    )
  }

  /**
   * Tracks Fit Analytics events.
   *
   * @description Sends a Google Analytics event when Fit Analytics is initiated.
   */
  const dispatchFitAnalyticsEvent = () => {
    useGASendEvent(
      fitAnalyticsSchema,
      {
        event: 'GAevent',
        eventID: '014',
        eventCategory: 'fitanalytics',
        eventAction: 'click_cta',
        eventLabel: 'start',
        eventElement01: '-',
        eventElement02: '-',
        eventElement03: '-',
      },
      { debug }
    )
  }

  /**
   * Tracks cookie banner interaction events.
   *
   * @param {CookieBannerLabels} label - The label of the cookie banner interaction.
   *
   * @description Sends a Google Analytics event when a user interacts with the cookie banner.
   */
  const dispatchCookieBannerEvent = (label: CookieBannerLabels) => {
    useGASendEvent(
      cookieBannerSchema,
      {
        event: 'GAevent',
        eventID: '016',
        eventCategory: 'banner_cookie',
        eventAction: 'click_cta',
        eventLabel: label,
        eventElement01: '-',
        eventElement02: '-',
        eventElement03: '-',
      },
      { debug }
    )
  }

  /**
   * Tracks social media link events.
   *
   * @param {string} label - The label of the social media platform.
   *
   * @description Sends a Google Analytics event when a social media link is clicked.
   */
  const dispatchSocialEvent = (label: string) => {
    useGASendEvent(
      socialSchema,
      {
        event: 'GAevent',
        eventID: '020',
        eventCategory: 'social',
        eventAction: 'click_cta',
        eventLabel: label as Socials,
        eventElement01: '-',
        eventElement02: '-',
        eventElement03: '-',
      },
      { debug }
    )
  }

  /**
   * Tracks help page events, typically associated with order support.
   *
   * @param {string} transactionId - The ID of the related transaction.
   * @param {string} currency - The currency of the transaction.
   * @param {number} amount - The transaction amount.
   *
   * @description Sends a Google Analytics event when a user accesses help or support.
   */
  const dispatchHelpEvent = (
    transactionId: string,
    currency: string,
    amount: number
  ) => {
    useGASendEvent(
      helpSchema,
      {
        event: 'GAevent',
        eventID: '026',
        eventCategory: 'thank_you_page',
        eventAction: 'click_cta',
        eventLabel: 'contact_us',
        eventElement01: transactionId,
        eventElement02: currency,
        eventElement03: amount,
      },
      { debug }
    )
  }

  return {
    dispatchGlobalNavigationEvent,
    dispatchHeaderEvent,
    dispatchMenuEvent,
    dispatchStoreLocatorEvent,
    dispatchSubMenuEvent,
    dispatchFooterEvent,
    dispatchFitAnalyticsEvent,
    dispatchCookieBannerEvent,
    dispatchSocialEvent,
    dispatchHelpEvent,
  }
}
